import { getText } from "shared/lib";
import {
  TreePropertyType,
  updateTreeProperty,
  type PropertyBoundaryTreePropertyDescriptor,
  type TreeDescriptor,
} from "../lib";
import { useTreeDialogValidator } from "./lib";

import { Toggle } from "shared/components";
import { FieldLabel } from "./FieldLabel";
import { FieldValidationMessages } from "./FieldValidationMessages";

export interface PropertyBoundaryFieldProps {
  onChange: (treeDescriptor: TreeDescriptor) => void;
  treeDescriptor: TreeDescriptor;
}

export function PropertyBoundaryField<
  T extends PropertyBoundaryTreePropertyDescriptor,
>({ onChange, treeDescriptor }: PropertyBoundaryFieldProps) {
  const { isValid, messages, shouldShowValidation } = useTreeDialogValidator<T>(
    {
      checker: (property) => !!property?.value,
      properties: treeDescriptor.properties,
      treePropertyType: TreePropertyType.property_boundary,
    },
  );

  const propertyBoundary = treeDescriptor.properties.get(
    TreePropertyType.property_boundary,
  ) as T | undefined;

  const handleChange = (value: string) => {
    const newTreeDescriptor = updateTreeProperty<T>(
      treeDescriptor,
      TreePropertyType.property_boundary,
      (draft) => {
        draft.value = value as T["value"];
      },
    );

    onChange?.(newTreeDescriptor);
  };

  return (
    <div className="flex flex-col gap-1">
      <div>
        <FieldLabel isValid={isValid}>
          {getText(
            "Is the tree within 6 metres of the property's front boundary, or within 4.5 metres of its rear boundary?",
          )}
        </FieldLabel>
        <p className="text-bodymd text-textSubdued">
          {getText(
            "Select 'Yes' if the tree is within either distance. This helps us determine if the tree contributes to shading surrounding areas.",
          )}
        </p>
      </div>

      <div className="flex gap-2">
        <Toggle
          aria-invalid={!isValid && shouldShowValidation}
          className="aria-invalid:border-redBorder"
          onClick={() => {
            handleChange("yes");
          }}
          pressed={propertyBoundary?.value === "yes"}
          variant="rounded"
        >
          {getText("Yes")}
        </Toggle>
        <Toggle
          aria-invalid={!isValid && shouldShowValidation}
          className="aria-invalid:border-redBorder"
          onClick={() => {
            handleChange("no");
          }}
          pressed={propertyBoundary?.value === "no"}
          variant="rounded"
        >
          {getText("No")}
        </Toggle>
        <Toggle
          aria-invalid={!isValid && shouldShowValidation}
          className="aria-invalid:border-redBorder"
          onClick={() => {
            handleChange("unsure");
          }}
          pressed={propertyBoundary?.value === "unsure"}
          variant="rounded"
        >
          {getText("Unsure")}
        </Toggle>
      </div>

      <FieldValidationMessages messages={messages} />
    </div>
  );
}
